import bookaMeetingApi from './request';
import { AxiosResponse } from 'axios';

// Updated to work with your new backend flow:
// 1. Call /api/whatsapp/file-upload to get pre-signed S3 URL
// 2. Upload file directly to S3 using pre-signed URL
// 3. Call Facebook uploader with S3 key

export interface MediaUploadResponse {
  success: boolean;
  mediaId?: string;
  mediaUrl?: string;
  file_handle?: string;
  upload_session_id?: string;
  s3Key?: string;
  error?: string;
  data?: {
    file_handle?: string;
    upload_session_id?: string;
  };
}

// API response interfaces
interface FileUploadResponse {
  success: boolean;
  message: string;
  data: {
    uploadUrl: string;
    fileKey: string;
    bucket: string;
    expiresIn: number;
    fileType: string;
    maxFileSize: number;
    supportedFormats: string[];
  };
  downloadUrl: string;
  metadata: {
    originalFileName: string;
    uploadTimestamp: string;
    fileCategory: string;
    description: string;
  };
}

interface MediaUploadApiResponse {
  success: boolean;
  message?: string;
  data?: {
    mediaId?: string;
    mediaUrl?: string;
    file_handle?: string;
    upload_session_id?: string;
    phoneNumberId?: string;
    s3Key?: string;
    mediaType?: string;
    fileTypeCategory?: string;
    createdAt?: string;
  };
  mediaUrl?: string;
  file_handle?: string;
  upload_session_id?: string;
  phoneNumberId?: string;
  s3Key?: string;
  mediaType?: string;
  fileTypeCategory?: string;
  createdAt?: string;
  error?: string;
}

interface MediaUrlApiResponse {
  success: boolean;
  mediaUrl?: string;
  error?: string;
}

// Delete user media function
export const deleteUserMedia = async (s3Key: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('=== DELETE USER MEDIA START ===');
    console.log('Deleting user media with S3 key:', s3Key);
    
    // Get phone number ID from session storage
    const phoneNumberId = sessionStorage.getItem('phoneNumberId');
    console.log('Phone Number ID:', phoneNumberId);
    
    if (!phoneNumberId) {
      return {
        success: false,
        error: 'Phone number ID not found in session storage',
      };
    }
    
    const response: AxiosResponse<{ success: boolean; message?: string; error?: string }> = await bookaMeetingApi.requestWIthAuth({
      method: 'DELETE',
      url: `/upload-user-media/${phoneNumberId}`,
      data: {
        key: s3Key,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    console.log('Delete API response status:', response.status);
    console.log('Delete API response data:', response.data);
    const responseData = response.data;

    if (responseData.success) {
      console.log('=== DELETE USER MEDIA SUCCESS ===');
      return {
        success: true,
      };
    } else {
      console.error('=== DELETE USER MEDIA FAILED ===');
      return {
        success: false,
        error: responseData.error || 'Failed to delete user media',
      };
    }
  } catch (error: any) {
    console.error('=== DELETE USER MEDIA ERROR ===');
    console.error('Delete user media error:', error);
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    return {
      success: false,
      error: 'Failed to delete media from user media service',
    };
  }
};

// Main upload function for file upload page - uses 3-step process
export const uploadMedia = async (file: File): Promise<MediaUploadResponse> => {
  try {
    console.log('Starting user media upload process for file:', file.name, 'size:', file.size, 'type:', file.type);
    
    // Step 1: Get pre-signed S3 upload URL from your backend
    const fileUploadResult = await getUserFileUploadUrl(file);
    
    if (!fileUploadResult.success) {
      return fileUploadResult;
    }

    // Step 2: Upload file directly to S3 using pre-signed URL
    const s3UploadResult = await uploadToS3(file, fileUploadResult.uploadUrl);
    
    if (!s3UploadResult.success) {
      return s3UploadResult;
    }

    // Step 3: Call /upload-user-media with S3 key
    const userMediaUploadResult = await uploadToUserMedia(fileUploadResult.fileKey, file.type);
    
    if (userMediaUploadResult.success) {
      return {
        success: true,
        mediaId: userMediaUploadResult.mediaId,
        mediaUrl: userMediaUploadResult.mediaUrl,
        file_handle: userMediaUploadResult.file_handle,
        upload_session_id: userMediaUploadResult.upload_session_id,
      };
    } else {
      return userMediaUploadResult;
    }

  } catch (error: any) {
    console.error('User media upload error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed. Please check your connection and try again.',
    };
  }
};

// WhatsApp-specific upload function - keeps the original flow for WhatsApp templates
export const uploadMediaForWhatsApp = async (file: File): Promise<MediaUploadResponse> => {
  try {
    console.log('Starting WhatsApp media upload process for file:', file.name, 'size:', file.size, 'type:', file.type);
    
    // Step 1: Get pre-signed S3 upload URL from your backend
    const fileUploadResult = await getFileUploadUrl(file);
    
    if (!fileUploadResult.success) {
      return fileUploadResult;
    }

    // Step 2: Upload file directly to S3 using pre-signed URL
    const s3UploadResult = await uploadToS3(file, fileUploadResult.uploadUrl);
    
    if (!s3UploadResult.success) {
      return s3UploadResult;
    }

    // Step 3: Call Facebook uploader with S3 key and actual file MIME type
    const whatsappUploadResult = await uploadToWhatsApp(fileUploadResult.fileKey, file.type);
    console.log('WhatsApp upload result:', whatsappUploadResult);
    if (whatsappUploadResult.success) {
      return {
        success: true,
        mediaId: whatsappUploadResult.mediaId,
        mediaUrl: whatsappUploadResult.mediaUrl,
        file_handle: whatsappUploadResult.file_handle,
        upload_session_id: whatsappUploadResult.upload_session_id,
      };
    } else {
      return whatsappUploadResult;
    }

  } catch (error: any) {
    console.error('WhatsApp media upload error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed. Please check your connection and try again.',
    };
  }
};

// Step 1: Get pre-signed S3 upload URL for user media upload
const getUserFileUploadUrl = async (file: File): Promise<{ success: boolean; uploadUrl?: string; fileKey?: string; fileType?: string; error?: string }> => {
  try {
    console.log('Getting pre-signed upload URL for user file:', file.name);
    
    const response: AxiosResponse<FileUploadResponse> = await bookaMeetingApi.requestWIthAuth({
      method: 'POST',
      url: '/whatsapp/file-upload',
      data: {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        folder: 'user-media',
        description: 'User uploaded media file'
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseData = response.data;

    if (responseData.success && responseData.data) {
      console.log('Pre-signed URL generated successfully for user file:', responseData.data.fileKey);
      return {
        success: true,
        uploadUrl: responseData.data.uploadUrl,
        fileKey: responseData.data.fileKey,
        fileType: responseData.data.fileType,
      };
    } else {
      return {
        success: false,
        error: 'Failed to generate upload URL',
      };
    }
  } catch (error: any) {
    console.error('Failed to get user upload URL:', error);
    return {
      success: false,
      error: 'Failed to get upload URL from server',
    };
  }
};

// Step 1: Get pre-signed S3 upload URL from your backend (WhatsApp templates)
const getFileUploadUrl = async (file: File): Promise<{ success: boolean; uploadUrl?: string; fileKey?: string; fileType?: string; error?: string }> => {
  try {
    console.log('Getting pre-signed upload URL for file:', file.name);
    
    const response: AxiosResponse<FileUploadResponse> = await bookaMeetingApi.requestWIthAuth({
      method: 'POST',
      url: '/whatsapp/file-upload',
      data: {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        folder: 'whatsapp-templates',
        description: 'WhatsApp template media upload'
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseData = response.data;

    if (responseData.success && responseData.data) {
      console.log('Pre-signed URL generated successfully:', responseData.data.fileKey);
      return {
        success: true,
        uploadUrl: responseData.data.uploadUrl,
        fileKey: responseData.data.fileKey,
        fileType: responseData.data.fileType,
      };
    } else {
      return {
        success: false,
        error: 'Failed to generate upload URL',
      };
    }
  } catch (error: any) {
    console.error('Failed to get upload URL:', error);
    return {
      success: false,
      error: 'Failed to get upload URL from server',
    };
  }
};

// Step 2: Upload file directly to S3 using pre-signed URL
const uploadToS3 = async (file: File, uploadUrl: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Uploading file to S3 using pre-signed URL');
    console.log('Upload URL:', uploadUrl);
    console.log('File details:', { name: file.name, type: file.type, size: file.size });
    
    // Upload directly to S3 using the pre-signed URL
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
      mode: 'cors', // Try to handle CORS
    });

    if (response.ok) {
      console.log('File uploaded to S3 successfully');
      return { success: true };
    } else {
      console.error('S3 upload failed:', response.status, response.statusText);
      console.error('Response headers:', response.headers);
      
      // Try to get error details
      let errorMessage = `S3 upload failed: ${response.status} ${response.statusText}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage += ` - ${errorText}`;
        }
      } catch (e) {
        // Ignore error reading response
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  } catch (error: any) {
    console.error('S3 upload error:', error);
    
    // Check if it's a CORS error
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      return {
        success: false,
        error: 'CORS error: Cannot upload directly to S3. Backend needs to configure S3 CORS settings.',
      };
    }
    
    return {
      success: false,
      error: `Failed to upload file to S3: ${error.message}`,
    };
  }
};

// Step 3: Call /upload-user-media with S3 key and phone number ID
const uploadToUserMedia = async (s3Key: string, mediaType: string): Promise<MediaUploadResponse> => {
  try {
    console.log('=== USER MEDIA UPLOAD START ===');
    console.log('Calling /upload-user-media with S3 key:', s3Key, 'mediaType:', mediaType);
    
    // Get phone number ID from session storage
    const phoneNumberId = sessionStorage.getItem('phoneNumberId');
    console.log('Phone Number ID:', phoneNumberId);
    
    if (!phoneNumberId) {
      return {
        success: false,
        error: 'Phone number ID not found in session storage',
      };
    }
    
    const response: AxiosResponse<MediaUploadApiResponse> = await bookaMeetingApi.requestWIthAuth({
      method: 'POST',
      url: `/upload-user-media/${phoneNumberId}`,
      data: {
        key: s3Key,
        mediaType: mediaType,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    console.log('User media API response status:', response.status);
    console.log('User media API response data:', response.data);
    const responseData = response.data;

    if (responseData.success && responseData.data) {
      return {
        success: true,
        mediaId: responseData.data.mediaId,
        mediaUrl: responseData.data.mediaUrl,
        file_handle: responseData.data.file_handle,
        upload_session_id: responseData.data.upload_session_id,
        s3Key: responseData.data.s3Key,
      };
    } else {
      console.error('=== USER MEDIA UPLOAD FAILED ===');
      return {
        success: false,
        error: 'Failed to upload to user media',
      };
    }
  } catch (error: any) {
    console.error('=== USER MEDIA UPLOAD ERROR ===');
    console.error('User media upload error:', error);
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    return {
      success: false,
      error: 'Failed to upload media to user media service',
    };
  }
};

// Step 3: Call Facebook uploader with S3 key (WhatsApp templates)
const uploadToWhatsApp = async (s3Key: string, mediaType: string): Promise<MediaUploadResponse> => {
  try {
    console.log('=== WHATSAPP UPLOAD START ===');
    console.log('Calling Facebook uploader with S3 key:', s3Key, 'mediaType:', mediaType);
    /*
    const response: AxiosResponse<MediaUploadApiResponse> = await bookaMeetingApi.requestWIthAuth({
      method: 'POST',
      url: '/upload-media', // Your Facebook uploader endpoint
      data: {
        key: s3Key,
        mediaType: mediaType, // Send actual MIME type like "image/jpeg"
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
  */
    // Call your backend API to upload media to WhatsApp
    
    const response: AxiosResponse<MediaUploadApiResponse> = await bookaMeetingApi.requestWIthAuth({
      method: 'POST',
      url: '/upload-media', // Your Facebook uploader endpoint
      data: {
        key: s3Key,
        mediaType: mediaType, // Send actual MIME type like "image/jpeg"
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
    console.log('Facebook API response status:', response.status);
    console.log('Facebook API response headers:', response.headers);
    console.log('Facebook API response data:', response.data);
    const responseData = response.data;
    
      /*
      const responseData = {
        "success": true,
        "file_handle": "4:MjAyNS0xMC0xNlQyMS01OS0zNi05NTBaLWI1NTM2ZmU3LTIwZWUtNDE0Mi05ZjQ0LTBlNmE5NmQyZDhjMC5qcGc=:aW1hZ2UvanBlZw==:ARbZLc1xP9hDkqaSovuBxsfQQV5zhgV4uQY-vt69ZqFo0RxT2cteON3_cDB6cchn2hFWjqRmy8fyP-tmD3C6WKjDwPyTaZDdCd4EyvchwVKA5w:e:1760997580:4137992306456289:61578824444731:ARbQd_9id6NHcwSOHyE",
        "upload_session_id": "MTphdHRhY2htZW50OjI1ZGQ0MjA2LTA1YzItNDNmNS1hMjg2LTFiZGZhNzFiZGZlNz9maWxlX25hbWU9MjAyNS0wOS0wM1QwMy00My01MS04NDRaLTFlYzZmMzMzLWNhNTEtNDJiZC1iMDhjLTJjYWFlZDViZGQ2MS5qcGcmZmlsZV9sZW5ndGg9ODY3MDExJmZpbGVfdHlwZT1pbWFnZSUyRmpwZWc=?sig=ARaTDqvSeI3S0Wti7fc",
        "message": "File uploaded successfully using Facebook Graph API"
    };
    */
    console.log('WhatsApp upload response:', responseData);

    if (responseData.success && responseData) {
      return {
        success: true,
        mediaId: responseData.file_handle,
        mediaUrl: responseData.upload_session_id,
        file_handle: responseData.file_handle,
        upload_session_id: responseData.upload_session_id,
      };
    } else {
      console.error('=== WHATSAPP UPLOAD FAILED ===');
      return {
        success: false,
        error: 'Failed to upload to WhatsApp',
      };
    }
  } catch (error: any) {
    console.error('=== WHATSAPP UPLOAD ERROR ===');
    console.error('Facebook upload error:', error);
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    return {
      success: false,
      error: 'Failed to upload media to WhatsApp',
    };
  }
};

export const getMediaUrl = async (mediaId: string): Promise<string | null> => {
  try {
    const response: AxiosResponse<MediaUrlApiResponse> = await bookaMeetingApi.requestWIthAuth({
      method: 'GET',
      url: `/media/${mediaId}`,
    });

    const responseData = response.data;

    if (responseData.success) {
      return responseData.mediaUrl || null;
    }
    return null;
  } catch (error) {
    console.error('Error fetching media URL:', error);
    return null;
  }
};

